import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { PurchaseOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { cn } from "@/lib/utils";

import {
  fetchQualityPurchaseOrderQuickSearch,
  type QualityPurchaseOrderSearchRow,
} from "./casesApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function purchaseOrderNumberLabel(row: Pick<QualityPurchaseOrderSearchRow, "id" | "number">): string {
  const number = row.number?.trim();
  return number != null && number !== "" ? number : `Purchase order ${row.id}`;
}

function purchaseOrderDescription(row: QualityPurchaseOrderSearchRow): string {
  return [row.supplier_name?.trim(), row.status?.trim()]
    .filter((part): part is string => part != null && part !== "")
    .join(" · ");
}

function toSearchOption(row: QualityPurchaseOrderSearchRow): SearchableSelectOption {
  return {
    value: String(row.id),
    label: purchaseOrderNumberLabel(row),
    description: purchaseOrderDescription(row) || null,
  };
}

export function QualityPurchaseOrderPicker({
  id,
  values,
  knownLabels,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  disabled?: boolean;
  onChange: (purchaseOrderIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityPurchaseOrderSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = values[0] != null ? String(values[0]) : "";
  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "purchase-order-quick-search", searchQuery],
    queryFn: () => fetchQualityPurchaseOrderQuickSearch(searchQuery),
    enabled: searchQuery.length >= 2 || (values.length > 0 && searchQuery === lookupQuery),
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((id) => ({
    id,
    chipLabel:
      knownRows.get(id) != null
        ? purchaseOrderNumberLabel(knownRows.get(id)!)
        : knownLabels?.[id]?.trim() || `Purchase order ${id}`,
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualityPurchaseOrderSearchRow>();
    for (const row of rows) {
      byId.set(row.id, row);
    }
    for (const id of values) {
      const selected = knownRows.get(id);
      if (selected != null) {
        byId.set(selected.id, selected);
      }
    }
    return [...byId.values()]
      .filter((row) => !values.includes(row.id))
      .map(toSearchOption);
  }, [searchResultsQuery.data?.rows, knownRows, values]);

  return (
    <div className="space-y-1.5">
      {selectedRows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedRows.map((selected) => (
            <span
              key={selected.id}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
            >
              <PurchaseOrderDetailLink
                purchaseOrderId={selected.id}
                purchaseOrderNumber={selected.chipLabel}
                className="text-xs"
              />
              <button
                type="button"
                className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
                aria-label={`Remove ${selected.chipLabel}`}
                disabled={disabled}
                onClick={() => onChange(values.filter((id) => id !== selected.id))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <SearchableSelect
        id={id}
        value=""
        onValueChange={(option) => {
          if (option == null) {
            return;
          }
          const parsed = Number(option.value);
          if (!Number.isFinite(parsed) || parsed < 1 || values.includes(parsed)) {
            return;
          }
          onChange([...values, parsed]);
          setSearchValue("");
        }}
        options={options}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder="Search GPO number, supplier, or SKU"
        searchPlaceholder="Search GPO number, supplier, or SKU"
        emptyMessage={
          searchValue.trim().length < 2
            ? "Type at least two characters to search purchase orders."
            : "No purchase orders match."
        }
        isLoading={searchResultsQuery.isFetching}
        disabled={disabled}
        panelMinWidth={320}
        className={cn(values.length > 0 ? "mt-0" : undefined)}
      />
    </div>
  );
}
